import { useCallback, useRef, useState } from 'react';
import { HIRES_VIEW_KEY } from '../constants/hiresViewTabs';

/**
 * THE view switching of the two hires pages that offer the Table
 * (`pages/DepartmentHires.js`, `pages/MyHires.js`) — the view strip's click
 * AND "open this hire", one definition for both pages.
 *
 * 1. `onViewChange(id)` — the view strip. Leaving the Table unmounts the
 *    expanded order form, so a DIRTY one ASKS first — the editor's three-way
 *    prompt — and the view switches on Save (ok) or Discard (the row
 *    expansion's own `continueWhenClean`, which it writes into `guardRef` —
 *    `HireOrderExpansion`'s `continueWhenCleanRef`). A click persists the view
 *    (`sfx_hires_view`). (Review round 4, R1: it silently discarded the edit;
 *    it was then refused with a pulse, and the owner, 2026-09-28: *"no pulse,
 *    no pop up, nothing changes when I try and click on Kaban, Gannt,
 *    Calendar"*.) `continueWhenClean(action)` is the same guard for the
 *    page's other Table-leaving clicks (the status tabs, "View order");
 *    outside the Table no form is mounted, so it simply runs.
 * 2. `openHire(itemOrId)` — clicking a HIRE in Kanban / Gantt / Calendar (owner,
 *    2026-09-27: *"it should take us to the table view in hires? and work!"*).
 *    Switches to the Table and hands the id to `useHireTableList`'s
 *    `requestItem` — the SAME resolver as the `?hire=` deep link (tab, page,
 *    search reset, expand), which also calls this. 🔑 It does NOT rewrite
 *    `sfx_hires_view`: following a hire is not a choice of view, exactly the
 *    deep link's rule ("a link is not a preference"). No dirty guard is
 *    needed on the way IN — outside the Table no order form is mounted — and
 *    inside the Table the expand itself refuses while another row is dirty.
 *    🔴 The ORDER affordances (the order-number link, "View order", the money
 *    dialog's "… & view order") still open the ORDER on Department Orders —
 *    owner: *"it is an order"*. This opens the HIRE.
 * 3. 🔀 THE OPEN HIRE STAYS OPEN ACROSS VIEWS (owner, 2026-09-28: *"I think it
 *    should stay open until we leave the hire section, close it ourselves or
 *    open another hire."*). `openItemId` is the hire the Table has expanded,
 *    reported by `HireOrderExpansion`'s `onExpandedItemChange` (wire
 *    `onExpandedItemChange` there). Kanban / Gantt / Calendar highlight it
 *    (`components/hires/hireHighlight.js`), and `onViewChange('table')`
 *    re-opens it through `requestItem` — the SAME resolver, never a second.
 *    It clears when the user collapses it (the expansion reports `null`) or
 *    opens another hire (it reports the new one). It lives in this hook's
 *    STATE alone, so leaving the Hires page (unmount) forgets it — nothing
 *    is written to storage, on hydration or ever. A hire no longer in the
 *    list (`hasItem` false — removed, or another department) is not
 *    re-requested, so no "could not be found" toast for a hire the user
 *    did not ask for.
 *
 * @param {object} args
 * @param {string} args.effectiveTab  the view on screen
 * @param {(id: string) => void} args.setActiveTab
 * @param {(itemId: number) => void} args.requestItem  `useHireTableList().requestItem`
 * @param {(itemId: number) => boolean} [args.hasItem]  is this hire still loaded
 * @returns {{ guardRef, continueWhenClean, onViewChange, openHire, openItemId, onExpandedItemChange }}
 */
export default function useHiresViewSwitch({ effectiveTab, setActiveTab, requestItem, hasItem }) {
  const guardRef = useRef(null);
  const [openItemId, setOpenItemId] = useState(null);

  const continueWhenClean = useCallback((action) => {
    if (guardRef.current) guardRef.current(action);
    else action();
  }, []);

  // The UNGUARDED switch, re-entered through a ref by a held click so it runs
  // against the post-save/discard state (and never re-enters the guard).
  const applyViewRef = useRef(null);
  const onViewChange = useCallback((id) => {
    if (effectiveTab === 'table' && id !== 'table' && guardRef.current) {
      guardRef.current(() => applyViewRef.current(id));
      return;
    }
    applyViewRef.current(id);
  }, [effectiveTab]);

  applyViewRef.current = (id) => {
    setActiveTab(id);
    try { window.localStorage.setItem(HIRES_VIEW_KEY, id); } catch (e) { /* private window */ }
    if (id === 'table' && effectiveTab !== 'table' && openItemId != null
      && (!hasItem || hasItem(openItemId))) {
      requestItem(openItemId);
    }
  };

  const openHire = useCallback((itemOrId) => {
    const id = itemOrId != null && typeof itemOrId === 'object' ? itemOrId.item_id : itemOrId;
    if (id == null) return;
    setActiveTab('table');
    requestItem(id);
  }, [setActiveTab, requestItem]);

  return { guardRef, continueWhenClean, onViewChange, openHire, openItemId, onExpandedItemChange: setOpenItemId };
}
